import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useToast } from "@/hooks/use-toast";
import { apiRequest } from "@/lib/queryClient";
import { X, Search } from "lucide-react";
import type { ServiceType, Company } from "@shared/schema";

const formSchema = z.object({
  serviceTypeId: z.string().min(1, "Service type is required"),
  rate: z.string().min(1, "Rate is required"),
  directCost: z.string().optional(),
  effectiveStartDate: z.string().min(1, "Start date is required"),
  effectiveEndDate: z.string().optional(),
  isActive: z.boolean().default(true),
  description: z.string().optional(),
});

type FormValues = z.infer<typeof formSchema>;

interface CreateCompanyServiceRateModalProps {
  open: boolean;
  onClose: () => void;
}

export function CreateCompanyServiceRateModal({ open, onClose }: CreateCompanyServiceRateModalProps) {
  const [selectedCompanyIds, setSelectedCompanyIds] = useState<number[]>([]);
  const [companySearch, setCompanySearch] = useState("");
  const [serviceTypeValue, setServiceTypeValue] = useState("");
  const [isActiveValue, setIsActiveValue] = useState(true);
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const { data: serviceTypes = [] } = useQuery<ServiceType[]>({
    queryKey: ['/api/service-types'],
  });

  const { data: companies = [] } = useQuery<Company[]>({
    queryKey: ['/api/companies', { context: "select" }],
    queryFn: async () => {
      const res = await fetch("/api/companies?limit=200", { credentials: "include" });
      if (!res.ok) throw new Error("Failed to fetch companies");
      return res.json();
    },
    select: (d: any) => d?.data ?? (Array.isArray(d) ? d : []),
  });

  const form = useForm<z.input<typeof formSchema>, unknown, FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      serviceTypeId: "",
      rate: "",
      directCost: "",
      effectiveStartDate: new Date().toISOString().split('T')[0],
      effectiveEndDate: "",
      isActive: true,
      description: "",
    },
  });

  const createMutation = useMutation({
    mutationFn: (data: FormValues & { companyIds: number[] }) =>
      apiRequest('POST', '/api/company-service-rates', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/company-service-rates'] });
      toast({
        title: "Success",
        description: "Custom rate created successfully",
      });
      handleClose();
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Failed to create custom rate",
        variant: "destructive",
      });
    },
  });

  const handleClose = () => {
    form.reset();
    setSelectedCompanyIds([]);
    setCompanySearch("");
    setServiceTypeValue("");
    setIsActiveValue(true);
    onClose();
  };

  const onSubmit = (data: FormValues) => {
    if (selectedCompanyIds.length === 0) {
      toast({
        title: "Error",
        description: "Please select at least one company",
        variant: "destructive",
      });
      return;
    }

    createMutation.mutate({
      ...data,
      effectiveEndDate: data.effectiveEndDate || undefined,
      directCost: data.directCost && data.directCost.trim() !== "" ? data.directCost : undefined,
      companyIds: selectedCompanyIds,
    });
  };

  const toggleCompany = (companyId: number) => {
    setSelectedCompanyIds(prev =>
      prev.includes(companyId)
        ? prev.filter(id => id !== companyId)
        : [...prev, companyId]
    );
  };

  const removeCompany = (companyId: number) => {
    setSelectedCompanyIds(prev => prev.filter(id => id !== companyId));
  };

  const filteredCompanies = companies.filter(company =>
    company.name.toLowerCase().includes(companySearch.toLowerCase())
  );

  const selectedCompanies = companies.filter(c => selectedCompanyIds.includes(c.id));

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Create Custom Rate</DialogTitle>
        </DialogHeader>

        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="serviceTypeId">Service *</Label>
              <Select
                value={serviceTypeValue}
                onValueChange={(value) => {
                  setServiceTypeValue(value);
                  form.setValue("serviceTypeId", value);
                }}
              >
                <SelectTrigger data-testid="select-service-type">
                  <SelectValue placeholder="Select service" />
                </SelectTrigger>
                <SelectContent>
                  {serviceTypes.map((type) => (
                    <SelectItem key={type.id} value={type.id}>
                      {type.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {form.formState.errors.serviceTypeId && (
                <p className="text-sm text-destructive">{form.formState.errors.serviceTypeId.message}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="rate">Rate (USD) *</Label>
              <Input
                id="rate"
                type="number"
                step="0.01"
                min="0"
                placeholder="0.00"
                {...form.register("rate")}
                data-testid="input-rate"
              />
              {form.formState.errors.rate && (
                <p className="text-sm text-destructive">{form.formState.errors.rate.message}</p>
              )}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="directCost">Direct Cost (USD) - Optional</Label>
              <Input
                id="directCost"
                type="number"
                step="0.01"
                min="0"
                placeholder="0.00"
                {...form.register("directCost")}
                data-testid="input-direct-cost"
              />
              <p className="text-xs text-muted-foreground">Internal cost (for reporting only)</p>
            </div>
            <div></div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="effectiveStartDate">Start Date *</Label>
              <Input
                id="effectiveStartDate"
                type="date"
                {...form.register("effectiveStartDate")}
                data-testid="input-start-date"
              />
              {form.formState.errors.effectiveStartDate && (
                <p className="text-sm text-destructive">{form.formState.errors.effectiveStartDate.message}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="effectiveEndDate">End Date (optional)</Label>
              <Input
                id="effectiveEndDate"
                type="date"
                {...form.register("effectiveEndDate")}
                data-testid="input-end-date"
              />
              <p className="text-xs text-muted-foreground">Leave empty for ongoing rate</p>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="description">Description (optional)</Label>
            <Textarea
              id="description"
              placeholder="e.g., Special rate for preferred customers"
              {...form.register("description")}
              data-testid="input-description"
            />
          </div>

          <div className="flex items-center space-x-2">
            <Checkbox
              id="isActive"
              checked={isActiveValue}
              onCheckedChange={(checked) => {
                setIsActiveValue(checked as boolean);
                form.setValue("isActive", checked as boolean);
              }}
              data-testid="checkbox-is-active"
            />
            <Label htmlFor="isActive">Active</Label>
          </div>

          <div className="space-y-3">
            <Label>Companies *</Label>
            
            {selectedCompanies.length > 0 && (
              <div className="flex flex-wrap gap-2 p-3 bg-muted/50 rounded-md">
                {selectedCompanies.map((company) => (
                  <Badge key={company.id} variant="secondary" className="flex items-center gap-1">
                    {company.name}
                    <button
                      type="button"
                      onClick={() => removeCompany(company.id)}
                      className="ml-1 hover:text-destructive"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </Badge>
                ))}
              </div>
            )}

            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search companies..."
                value={companySearch}
                onChange={(e) => setCompanySearch(e.target.value)}
                className="pl-10"
                data-testid="input-company-search"
              />
            </div>

            <ScrollArea className="h-48 border rounded-md">
              <div className="p-2 space-y-1">
                {filteredCompanies.length === 0 ? (
                  <p className="text-sm text-muted-foreground text-center py-4">No companies found</p>
                ) : (
                  filteredCompanies.map((company) => {
                    const isSelected = selectedCompanyIds.includes(company.id);
                    return (
                      <label
                        key={company.id}
                        className={`flex items-center space-x-2 p-2 rounded-md cursor-pointer hover:bg-muted ${
                          isSelected ? 'bg-muted' : ''
                        }`}
                        data-testid={`company-option-${company.id}`}
                      >
                        <Checkbox
                          checked={isSelected}
                          onCheckedChange={() => toggleCompany(company.id)}
                        />
                        <div className="flex-1">
                          <p className="text-sm font-medium">{company.name}</p>
                          <p className="text-xs text-muted-foreground">{company.type}</p>
                        </div>
                      </label>
                    );
                  })
                )}
              </div>
            </ScrollArea>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={handleClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={createMutation.isPending} data-testid="button-submit">
              {createMutation.isPending ? "Creating..." : "Create Custom Rate"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
